import {useNavigate, useRouterState} from '@tanstack/react-router';

import {Banner} from '@/components/Banner';
import {Button} from '@/components/ui/button';
import {canAccessRoute} from '@/lib/permissions';
import {takeNewBuild, useSwUpdateStore} from '@/lib/sw-update';
import {useAuthStore} from '@/store/auth';

const WHATS_NEW_PATH = '/whats-new';

/**
 * Offers the reload that moves this tab onto a newly deployed build.
 *
 * Without it users stay on the old build indefinitely: the app registers its
 * service worker with `registerType: 'prompt'`, so the new worker installs and
 * then parks in `waiting` until something calls `updateSW`. Nothing did, which
 * is DERP-266 — the version only changed once every tab closed, and meanwhile
 * a plain refresh was answered from the old precache, so only a hard refresh
 * appeared to work.
 *
 * Prompting rather than reloading on its own is deliberate: this app is used
 * for data entry (job reports, advance requests), and reloading underneath
 * someone mid-form would discard what they had typed. It renders as a banner
 * rather than a `sonner` toast because `<Toaster />` is mounted inside
 * `_authenticated`, so a toast would be invisible on `/login` — exactly where a
 * user who has just been away is likely to be.
 *
 * In practice most tabs never get this far: `installUpdateOnNavigate` takes
 * the update on the next route change, which for normal use arrives first.
 * This is the fallback for someone parked on one screen, and for anyone who
 * would rather take the reload on their own terms.
 *
 * **"See what's new" reloads too, and that is the point.** It navigates rather
 * than linking, so `installUpdateOnNavigate` sees the path change and calls
 * `takeNewBuild()` — the tab lands on What's New *on the new build*, which is
 * the only state where the page can show the release it is announcing. A plain
 * `<a href>` would be wrong here for the DERP-266 reason: the old worker
 * answers a document request from its own precache, so the full page load
 * could serve the stale build back.
 */
export function UpdatePrompt() {
  const {updateAvailable, isApplying} = useSwUpdateStore();
  const navigate = useNavigate();
  const pathname = useRouterState({select: (state) => state.location.pathname});
  // The same gate the sidebar uses, so the button cannot offer a page the user
  // would be bounced off. It answers false with no signed-in user, which is
  // what keeps it off `/login`, and false for external users, whose grant
  // surface is their route whitelist.
  const canSeeWhatsNew = useAuthStore((state) => canAccessRoute(WHATS_NEW_PATH, state.user));

  if (!updateAvailable) return null;

  // No point offering to go somewhere they already are — and a same-path
  // navigation resolves with `pathChanged: false`, so it would not take the
  // update either. On this page the Reload button is the whole story.
  const offerWhatsNew = canSeeWhatsNew && pathname !== WHATS_NEW_PATH;

  return (
    <Banner
      position="bottom"
      role="status"
      actions={
        <>
          {offerWhatsNew ? (
            <Button
              variant="outline"
              disabled={isApplying}
              onClick={() => void navigate({to: WHATS_NEW_PATH})}
            >
              See what&apos;s new
            </Button>
          ) : null}
          <Button onClick={() => void takeNewBuild()} disabled={isApplying}>
            {isApplying ? 'Reloading…' : 'Reload'}
          </Button>
        </>
      }
    >
      <p>
        A new version of Dyna ERP is available. Reload to pick up the latest changes.
        {offerWhatsNew ? " What's new lists what changed in each release." : ''}
      </p>
    </Banner>
  );
}
